import { z } from "zod";
import { accountIssueStatusSchema } from "./account-issue";
import { baseEntitySchema } from "./base";
import { StatusTierSchema } from "../dtos/status-tier";
import {
  inventoryAccountStatusSchema,
  inventoryTwoFactorStatusSchema,
  manualAccountStatusSchema,
} from "./inventory-account-state";

export const inventoryStatusSchema = z.enum([
  "draft",
  "active",
  "depleted",
  "closed",
  "blocked",
  "archived",
]);
export type InventoryStatus = z.infer<typeof inventoryStatusSchema>;

export const inventorySourceDealSnapshotSchema = z.object({
  dealId: z.string(),
  dealName: z.string().optional(),
  customerId: z.string(),
  quantity: z.number().nonnegative(),
  amount: z.number().nonnegative(),
  unitPrice: z.number().nonnegative(),
  buyOrSell: z.string().optional(),
  type: z.string().optional(),
  stage: z.number().optional(),
  statusTier: StatusTierSchema.optional(),
  ipLocation: z.string().optional(),
  accountEmail: z.string().optional(),
  accountPassword: z.string().optional(),
  paymentMethod: z.string().optional(),
  paymentDetails: z.string().optional(),
  paypalEmail: z.string().optional(),
  paymentOption: z.string().optional(),
  sourceTemporaryDealId: z.string().optional(),
});
export type InventorySourceDealSnapshot = z.infer<
  typeof inventorySourceDealSnapshotSchema
>;

export const inventoryDataSchema = z.object({
  name: z.string().min(1),
  productId: z.string(),
  productName: z.string(),
  status: inventoryStatusSchema,
  bookingBlockedUntil: z.preprocess(
    (value) => value ?? null,
    z.date().nullable(),
  ),
  bookingBlockedBySalesOrderId: z.preprocess(
    (value) => value ?? null,
    z.string().nullable(),
  ),
  bookingBlockedTbd: z.boolean().optional(),
  problemStatus: accountIssueStatusSchema.optional(),
  accountOwnerName: z.string().optional(),
  accountStatus: inventoryAccountStatusSchema.default("needs_reset"),
  // Admin-controlled base status; the effective `accountStatus` is derived from it.
  manualAccountStatus: manualAccountStatusSchema.default("active"),
  accountEmail: z.string().optional(),
  accountPassword: z.string().optional(),
  paypalEmail: z.string().optional(),
  statusTier: StatusTierSchema.optional(),
  dob: z.date().optional(),
  ipLocation: z.string().optional(),
  lastBooking: z.date().optional(),
  twoFaStatus: inventoryTwoFactorStatusSchema.default("unknown"),
  cardTypeTag: z.string().optional(),
  lastAccessVerifiedAt: z.date().optional(),
  gologinProfileId: z.preprocess(
    (value) => value ?? null,
    z.string().nullable(),
  ),
  username: z.string().optional(),
  expirationInfo: z.string().optional(),
  notes: z.string().optional(),
  sourceDealSnapshot: inventorySourceDealSnapshotSchema.optional(),
  totalPurchasedMiles: z.number().nonnegative().default(0),
  totalSoldMiles: z.number().nonnegative().default(0),
  availableMiles: z.number().default(0),
  accountBalance: z.number().nullable().default(null),
  // Derived snapshot maintained by the account-status recompute trigger so the UI
  // can explain a write-off. accountValue (USD) = (buyCpm ?? 0) × availableMiles.
  accountValue: z.number().nullable().default(null),
  buyCpm: z.number().nullable().default(null),
  purchaseOrderCount: z.number().int().nonnegative().default(0),
  sellOrderCount: z.number().int().nonnegative().default(0),
  archivedFromStatus: inventoryStatusSchema.optional(),
});

export type InventoryData = z.infer<typeof inventoryDataSchema>;
export const inventorySchema = baseEntitySchema.merge(inventoryDataSchema);
export type Inventory = z.infer<typeof inventorySchema>;
